import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hook do blokowania wyjścia z aplikacji podczas pracy
 * Obsługuje zarówno przycisk "Wstecz" przeglądarki jak i zamknięcie karty
 * 
 * @param isWorkActive - czy praca jest aktywna (powinien blokować wyjście)
 */
export const useExitBlocker = (isWorkActive: boolean) => {
  const { t } = useTranslation();
  const isWorkActiveRef = useRef(isWorkActive);
  const historyStateRef = useRef<{ key: string } | null>(null);

  // Aktualizuj ref przy każdej zmianie stanu
  useEffect(() => {
    isWorkActiveRef.current = isWorkActive;
  }, [isWorkActive]);

  useEffect(() => {
    if (!isWorkActive) {
      // Jeśli praca nie jest aktywna, usuń wpis z historii jeśli istnieje
      if (historyStateRef.current) {
        historyStateRef.current = null;
      }
      return;
    }

    // Obsługa przedunload (zamknięcie karty/przeglądarki)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Wymagane dla większości przeglądarek
      const message = t('property.exitWarning') || 'Czy na pewno chcesz opuścić aplikację? Twoje postępy w sprzątaniu mogą nie zostać zapisane.';
      e.returnValue = message;
      return message;
    };

    // Obsługa przycisku "Wstecz" przeglądarki (popstate)
    const handlePopState = (e: PopStateEvent) => {
      // Sprawdź czy praca jest nadal aktywna (używamy ref, aby uniknąć stale closure)
      if (isWorkActiveRef.current) {
        const message = t('property.exitWarning') || 'Czy na pewno chcesz opuścić aplikację? Twoje postępy w sprzątaniu mogą nie zostać zapisane.';
        
        // Wyświetl okno potwierdzenia
        const confirmed = window.confirm(message);
        
        if (!confirmed) {
          // Jeśli użytkownik anulował, przywróć poprzedni stan historii
          // Dodaj nowy wpis do historii, aby zablokować cofnięcie
          const currentState = { key: Date.now().toString() };
          historyStateRef.current = currentState;
          window.history.pushState(currentState, '', window.location.href);
        }
        // Jeśli użytkownik potwierdził, pozwól na wyjście (nie rób nic)
      }
    };

    // Dodaj wpis do historii tylko raz, aby móc przechwycić popstate
    if (!historyStateRef.current) {
      const currentState = { key: Date.now().toString() };
      historyStateRef.current = currentState;
      window.history.pushState(currentState, '', window.location.href);
    }

    // Dodaj listenery
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isWorkActive, t]);
};
